import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  GraduationCap,
  Sparkles,
  Coins,
  Clock,
  Trophy,
  ArrowRight,
  Loader2,
  PawPrint,
  Target,
  Zap,
  Flame,
  Lock,
  CheckCircle2,
  Check,
  ChevronRight,
  ChevronLeft,
  Star,
  Calendar,
} from 'lucide-react';
import { apiClient } from '@/api/axios';
import { usePets } from '../../context/PetsContext';
import { useAuth } from '@/context/AuthContext';
import { type WeekData } from './WeekSelector';
import { type TaskData } from './TaskCard';
import { type StreakBadge } from './StreakTracker';
import { type Badge } from './BadgeShowcase';
import { toast } from 'sonner';

// ---------- types ----------
interface TrainingProgram {
  id: string;
  name: string;
  description: string;
  totalWeeks: number;
  weeks: WeekData[];
  dailyCommitment: string;
  totalRewards: number;
}

interface TrainingProgress {
  enrolled: boolean;
  enrollmentId?: string;
  programId?: string;
  currentWeek: number;
  completedTasks: string[];
  streak: { current: number; longest: number; badges: StreakBadge[] };
  totalPetsEarned: number;
  badges: Badge[];
  weekTasks: Record<number, TaskData[]>;
}

// ---------- default data ----------
const DEFAULT_WEEKS: WeekData[] = [
  { week: 1, title: 'Bond & Trust', theme: 'Build connection' },
  { week: 2, title: 'Basic Commands', theme: 'Sit, stay, come' },
  { week: 3, title: 'Routine', theme: 'Structure & discipline' },
  { week: 4, title: 'Play & Stimulation', theme: 'Mental enrichment' },
  { week: 5, title: 'Social Skills', theme: 'Meet & greet' },
  { week: 6, title: 'Health & Care', theme: 'Grooming comfort' },
  { week: 7, title: 'Advanced Commands', theme: 'Recall & drop' },
  { week: 8, title: 'Outdoor Behaviour', theme: 'Leash & control' },
  { week: 9, title: 'Tricks & Intelligence', theme: 'Fun & agility' },
  { week: 10, title: 'Mastery', theme: 'Certification' },
];

const DEFAULT_TASKS: TaskData[] = [
  { id: 'p-1', day: 1, title: 'Calm Interaction', description: 'Spend 5 mins of calm, gentle interaction with your pet.', durationMinutes: 5, pointsReward: 0 },
  { id: 'p-2', day: 2, title: 'Name Recognition', description: 'Call your pet\'s name and reward when they look at you.', durationMinutes: 5, pointsReward: 0 },
  { id: 'p-3', day: 3, title: 'Eye Contact', description: 'Hold a treat near your eyes. Reward when your pet makes eye contact.', durationMinutes: 5, pointsReward: 0 },
  { id: 'p-4', day: 4, title: 'Gentle Handling', description: 'Touch paws, ears, and mouth gently while rewarding calm behaviour.', durationMinutes: 7, pointsReward: 0 },
  { id: 'p-5', day: 5, title: 'Follow Me', description: 'Walk around the house and reward your pet for following you.', durationMinutes: 5, pointsReward: 0 },
  { id: 'p-6', day: 6, title: 'Play Session', description: 'Free play with your pet\'s favourite toy for bonding.', durationMinutes: 10, pointsReward: 0 },
  { id: 'p-7', day: 7, title: 'Week Review', description: 'Repeat your pet\'s favourite activity from this week.', durationMinutes: 10, pointsReward: 0 },
];

const WEEK_ICONS = ['🐾', '🎯', '📋', '🎾', '🐕', '🏥', '🧠', '🚶', '🧩', '🏆'];

// ---------- component ----------
export function TrainingDashboard() {
  const { t } = useTranslation();
  const { selectedPet, pets } = usePets();
  useAuth();

  const [program, setProgram] = useState<TrainingProgram | null>(null);
  const [progress, setProgress] = useState<TrainingProgress | null>(null);
  const [selectedWeek, setSelectedWeek] = useState(1);
  const [showWeekDetail, setShowWeekDetail] = useState(false);
  const [showEnrolledDashboard, setShowEnrolledDashboard] = useState(false);
  const [isLoadingProgram, setIsLoadingProgram] = useState(true);
  const [isLoadingProgress, setIsLoadingProgress] = useState(false);
  const [isEnrolling, setIsEnrolling] = useState(false);
  const [completingTaskId, setCompletingTaskId] = useState<string | null>(null);

  // Helper: transform backend progress response into frontend TrainingProgress
  const mapProgressResponse = useCallback((data: any): TrainingProgress => {
    const enrollment = data.enrollment;
    const completions = data.completions ?? [];
    const streak = data.streak ?? { currentStreak: 0, longestStreak: 0 };
    const badges = data.badges ?? [];
    const currentWeekTasks = (data.currentWeekTasks ?? []).map((t: any) => ({
      id: t.id, day: t.dayNumber ?? t.sortOrder ?? 1, title: t.title,
      description: t.description ?? t.instructions ?? '', durationMinutes: t.durationMinutes ?? 5,
      pointsReward: t.pointsReward ?? 0, completed: t.completed ?? false,
    }));
    const completedTaskIds = completions.map((c: any) => c.taskId);
    const totalPetsEarned = completions.reduce((sum: number, c: any) => sum + (c.pointsAwarded ?? 0), 0);

    return {
      enrolled: true,
      enrollmentId: enrollment?.id,
      programId: enrollment?.programId,
      currentWeek: enrollment?.currentWeek ?? 1,
      completedTasks: completedTaskIds,
      streak: { current: streak.currentStreak ?? 0, longest: streak.longestStreak ?? 0, badges: [] },
      totalPetsEarned,
      badges: badges.map((b: any) => ({ type: b.badgeType, name: b.name, icon: b.icon })),
      weekTasks: { [enrollment?.currentWeek ?? 1]: currentWeekTasks },
    };
  }, []);

  // Fetch active program
  useEffect(() => {
    let cancelled = false;
    setIsLoadingProgram(true);
    apiClient.get('/training/programs')
      .then((res) => {
        if (cancelled) return;
        const programs = res.data?.programs ?? (Array.isArray(res.data) ? res.data : []);
        setProgram(programs[0] ?? null);
      })
      .catch(() => { if (!cancelled) setProgram(null); })
      .finally(() => { if (!cancelled) setIsLoadingProgram(false); });
    return () => { cancelled = true; };
  }, []);

  // Fetch progress for selected pet
  useEffect(() => {
    if (!selectedPet?.id) return;
    let cancelled = false;
    setIsLoadingProgress(true);
    apiClient.get(`/training/progress?petId=${selectedPet.id}`)
      .then((res) => {
        if (cancelled) return;
        if (res.data?.enrollment) {
          const mapped = mapProgressResponse(res.data);
          setProgress(mapped);
          setSelectedWeek(mapped.currentWeek);
        } else {
          setProgress(null);
        }
      })
      .catch(() => { if (!cancelled) setProgress(null); })
      .finally(() => { if (!cancelled) setIsLoadingProgress(false); });
    return () => { cancelled = true; };
  }, [selectedPet?.id, mapProgressResponse]);

  const handleEnroll = useCallback(async () => {
    if (!selectedPet?.id || !program?.id) { toast.error(t('training.selectPetFirst', 'Please select a pet first')); return; }
    setIsEnrolling(true);
    try {
      await apiClient.post(`/training/programs/${program.id}/enroll`, { petId: selectedPet.id });
      // Re-fetch progress to get real data from backend
      const progressRes = await apiClient.get(`/training/progress?petId=${selectedPet.id}`);
      if (progressRes.data?.enrollment) {
        const mapped = mapProgressResponse(progressRes.data);
        setProgress(mapped);
        setSelectedWeek(1);
      }
      setShowEnrolledDashboard(true); setShowWeekDetail(true); toast.success(t('training.journeyStarted', 'Training journey started!'));
    } catch (err: any) {
      toast.error(err?.response?.data?.error || t('training.failedEnroll', 'Failed to enroll'));
    } finally { setIsEnrolling(false); }
  }, [selectedPet?.id, program?.id, mapProgressResponse]);

  const handleCompleteTask = useCallback(async (taskId: string) => {
    if (!selectedPet?.id || !progress?.enrollmentId) return;
    setCompletingTaskId(taskId);
    try {
      const res = await apiClient.post(`/training/tasks/${taskId}/complete`, { enrollmentId: progress.enrollmentId });
      const { pointsAwarded, streakUpdate, weekCompleted } = res.data;
      // Update local state optimistically
      setProgress((prev) => {
        if (!prev) return prev;
        const updated = {
          ...prev,
          completedTasks: [...prev.completedTasks, taskId],
          totalPetsEarned: prev.totalPetsEarned + (pointsAwarded ?? 0),
          streak: { ...prev.streak, current: streakUpdate?.currentStreak ?? prev.streak.current, longest: streakUpdate?.longestStreak ?? prev.streak.longest },
          weekTasks: { ...prev.weekTasks, [prev.currentWeek]: (prev.weekTasks[prev.currentWeek] ?? []).map(t => t.id === taskId ? { ...t, completed: true } : t) },
        };
        if (weekCompleted) {
          updated.currentWeek = prev.currentWeek + 1;
        }
        return updated;
      });
      toast.success(`${t('training.taskCompleted', 'Task completed!')}${pointsAwarded > 0 ? ` +${pointsAwarded} PETS` : ''}`);
    } catch (err: any) {
      toast.error(err?.response?.data?.error || 'Failed to complete task');
    } finally { setCompletingTaskId(null); }
  }, [selectedPet?.id, progress?.enrollmentId]);

  const weeks = program?.weeks ?? DEFAULT_WEEKS;
  const enrolled = progress?.enrolled ?? false;
  const currentWeek = progress?.currentWeek ?? 1;
  const completedTasks = new Set(progress?.completedTasks ?? []);
  const weekCompletions: Record<number, number> = {};
  weeks.forEach((w) => {
    const tasks = progress?.weekTasks?.[w.week] ?? [];
    weekCompletions[w.week] = tasks.filter((t) => completedTasks.has(t.id)).length;
  });
  const currentTasks = progress?.weekTasks?.[selectedWeek] ?? (selectedWeek === 1 ? DEFAULT_TASKS : []);

  if (isLoadingProgram) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="text-center">
          <div className="w-10 h-10 border-3 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
          <p className="text-gray-500 dark:text-gray-400 text-sm" style={{ fontFamily: 'Inter, sans-serif' }}>{t('training.loading', 'Loading training programs...')}</p>
        </div>
      </div>
    );
  }

  // ==================== LANDING PAGE (not enrolled, or enrolled but viewing overview) ====================
  if (!enrolled || !showEnrolledDashboard) {
    return (
      <div className="max-w-3xl mx-auto space-y-5">
        {/* Hero Card */}
        <div className="rounded-2xl p-6 md:p-8 relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #594BFF, #6C5CE7, #8B5CF6)' }}>
          <div className="absolute top-0 right-0 w-48 h-48 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
          <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/4" />

          <div className="relative flex flex-col md:flex-row md:items-center gap-6">
            <div className="flex-1">
              <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white/15 text-white/90 text-[11px] font-medium mb-3">
                <Sparkles className="w-3 h-3" /> {t('training.tenWeekProgram', '10-Week Program')}
              </div>
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-2 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('training.trainBondEarn', 'Train. Bond.')} <span className="text-yellow-300">{t('training.earn', 'Earn.')}</span>
              </h1>
              <p className="text-white/75 text-sm mb-5 max-w-sm leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                {t('training.description', 'Daily micro-training sessions that strengthen your bond and earn PETS tokens.')}
              </p>
              {enrolled ? (
                <button
                  type="button"
                  onClick={() => { setShowEnrolledDashboard(true); setShowWeekDetail(true); setSelectedWeek(currentWeek); }}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-xl font-bold text-sm hover:bg-white/95 transition-all shadow-lg shadow-black/15 active:scale-[0.97]"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {t('training.continueWeek', 'Continue Week {{week}}', { week: currentWeek })} <ArrowRight className="w-4 h-4" />
                </button>
              ) : (
                <button
                  type="button"
                  onClick={handleEnroll}
                  disabled={isEnrolling || !selectedPet}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-white text-purple-600 rounded-xl font-bold text-sm hover:bg-white/95 transition-all shadow-lg shadow-black/15 active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{ fontFamily: 'Sora, sans-serif' }}
                >
                  {isEnrolling ? <><Loader2 className="w-4 h-4 animate-spin" /> {t('training.starting', 'Starting...')}</> : <>{t('training.startJourney', 'Start Training Journey')} <ArrowRight className="w-4 h-4" /></>}
                </button>
              )}
              {!enrolled && !selectedPet && pets.length > 0 && <p className="text-white/50 text-xs mt-2">{t('training.selectPetToBegin', 'Select a pet from the sidebar to begin')}</p>}
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-2 md:flex md:flex-col md:gap-2 md:w-44">
              {[
                { icon: <Clock className="w-4 h-4" />, label: t('training.minPerDay', '5-10 min/day'), sub: t('training.quickSessions', 'Quick sessions') },
                { icon: <Coins className="w-4 h-4 text-yellow-300" />, label: 'PETS', sub: t('training.earnRewards', 'Earn rewards') },
                { icon: <Trophy className="w-4 h-4 text-amber-300" />, label: t('training.badges', 'Badges'), sub: t('training.toUnlock', 'To unlock') },
              ].map((s, i) => (
                <div key={i} className="flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-lg px-3 py-2.5">
                  <div className="w-8 h-8 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 text-white">{s.icon}</div>
                  <div className="min-w-0">
                    <p className="text-white font-semibold text-xs truncate">{s.label}</p>
                    <p className="text-white/50 text-[10px] truncate">{s.sub}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Program Overview - Compact Week Grid */}
        <div className="rounded-2xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-base font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('training.programRoadmap', 'Program Roadmap')}</h3>
            <span className="text-xs text-gray-400 dark:text-gray-500">{t('training.tasksTotal', '{{count}} tasks total', { count: 70 })}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
            {weeks.map((w, i) => {
              const isCurrent = enrolled && w.week === currentWeek;
              const isCompleted = enrolled && w.week < currentWeek;
              const isLocked = enrolled && w.week > currentWeek;
              return (
                <div
                  key={w.week}
                  onClick={() => { if (enrolled && !isLocked) { setShowEnrolledDashboard(true); setShowWeekDetail(true); setSelectedWeek(w.week); } }}
                  className={`group rounded-xl border p-3 transition-all ${
                    isCurrent
                      ? 'bg-purple-50 dark:bg-purple-900/15 border-purple-300 dark:border-purple-700 cursor-pointer'
                      : isCompleted
                        ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40 cursor-pointer'
                        : isLocked
                          ? 'bg-gray-50 dark:bg-white/5 border-gray-100 dark:border-white/5 opacity-50'
                          : 'bg-gray-50 dark:bg-white/5 border-gray-100 dark:border-white/5 hover:border-purple-500/30 hover:bg-purple-600/5 dark:hover:bg-purple-600/10 cursor-default'
                  }`}
                >
                  <div className="flex items-center gap-1.5 mb-1.5">
                    <span className="text-base">{isCompleted ? '✅' : isLocked ? '🔒' : WEEK_ICONS[i]}</span>
                    <span className="text-[10px] font-bold text-purple-600 dark:text-purple-400 uppercase tracking-wide">{t('training.wk', 'WK {{num}}', { num: w.week })}</span>
                  </div>
                  <p className="text-xs font-semibold text-gray-800 dark:text-gray-200 leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>{t(`training.week${w.week}Title`, w.title)}</p>
                  <p className="text-[10px] text-gray-400 dark:text-gray-500 mt-0.5">{t(`training.week${w.week}Theme`, w.theme)}</p>
                  {isCurrent && (
                    <p className="text-[10px] font-semibold text-purple-600 dark:text-purple-400 mt-1.5">{t('training.continue', 'Continue →')}</p>
                  )}
                  {isCompleted && (
                    <p className="text-[10px] font-medium text-emerald-600 dark:text-emerald-400 mt-1.5">{t('training.completed', 'Completed')}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom Stats Row */}
        <div className="grid grid-cols-3 gap-3">
          {[
            { icon: <Target className="w-5 h-5 text-purple-600" />, value: '10', label: t('training.weeks', 'Weeks'), bg: 'bg-purple-600/10 dark:bg-purple-600/15' },
            { icon: <Zap className="w-5 h-5 text-emerald-500" />, value: '70', label: t('training.tasks', 'Tasks'), bg: 'bg-emerald-500/8 dark:bg-emerald-500/15' },
            { icon: <Coins className="w-5 h-5 text-amber-500" />, value: '10', label: t('training.badges', 'Badges'), bg: 'bg-amber-500/8 dark:bg-amber-500/15' },
          ].map((s, i) => (
            <div key={i} className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 px-4 py-3 flex items-center gap-3">
              <div className={`w-10 h-10 rounded-xl ${s.bg} flex items-center justify-center flex-shrink-0`}>{s.icon}</div>
              <div>
                <span className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{s.value}</span>
                <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-0.5">{s.label}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  // ==================== ENROLLED ====================
  const totalTasks = weeks.length * 7;
  const completedCount = completedTasks.size;
  const overallPercent = Math.round((completedCount / totalTasks) * 100);

  return (
    <div className="max-w-3xl mx-auto space-y-4">
      {/* Progress Header */}
      <div className="rounded-2xl p-5 text-white relative overflow-hidden" style={{ background: 'linear-gradient(to right, #594BFF, #7C6FFF)' }}>
        <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/4" />
        <div className="relative flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-white/15 flex items-center justify-center flex-shrink-0">
            <GraduationCap className="w-6 h-6" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center justify-between mb-1">
              <h2 className="text-base font-bold truncate" style={{ fontFamily: 'Sora, sans-serif' }}>{t('training.petTraining', "{{name}}'s Training", { name: selectedPet?.name || t('training.pet', 'Pet') })}</h2>
              <span className="text-xs text-white/60 flex-shrink-0">{t('training.weekNum', 'Week {{num}}', { num: currentWeek })}/{weeks.length}</span>
            </div>
            <div className="w-full bg-white/20 rounded-full h-2 mb-1">
              <div className="bg-white rounded-full h-2 transition-all duration-700 ease-out" style={{ width: `${overallPercent}%` }} />
            </div>
            <div className="flex items-center justify-between text-[11px] text-white/60">
              <span>{t('training.ofTasks', '{{done}} of {{total}} tasks', { done: completedCount, total: totalTasks })}</span>
              <span>{overallPercent}%</span>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Row */}
      <div className="grid grid-cols-3 gap-2">
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 px-3 py-2.5 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-amber-500/10 dark:bg-amber-500/15 flex items-center justify-center flex-shrink-0">
            <Coins className="w-4.5 h-4.5 text-amber-500" />
          </div>
          <div>
            <span className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{progress?.totalPetsEarned ?? 0}</span>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-0.5">{t('training.petsEarned', 'PETS earned')}</p>
          </div>
        </div>
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 px-3 py-2.5 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-orange-500/10 dark:bg-orange-500/15 flex items-center justify-center flex-shrink-0">
            <Flame className="w-4.5 h-4.5 text-orange-500" />
          </div>
          <div>
            <span className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{progress?.streak.current ?? 0}</span>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-0.5">{t('training.dayStreak', 'Day streak')}</p>
          </div>
        </div>
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 px-3 py-2.5 flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-lg bg-purple-600/10 dark:bg-purple-600/15 flex items-center justify-center flex-shrink-0">
            <Star className="w-4.5 h-4.5 text-purple-600" />
          </div>
          <div>
            <span className="text-lg font-bold text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{progress?.badges?.length ?? 0}</span>
            <p className="text-[10px] text-gray-400 dark:text-gray-500 -mt-0.5">{t('training.badges', 'Badges')}</p>
          </div>
        </div>
      </div>

      {/* Week Selector - Compact Horizontal */}
      <div>
        <div className="flex items-center justify-between mb-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white" style={{ fontFamily: 'Sora, sans-serif' }}>{t('training.weeks', 'Weeks')}</h3>
          <span className="text-[11px] text-gray-400 dark:text-gray-500">{t('training.completedCount', '{{count}} completed', { count: currentWeek - 1 })}</span>
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1 scrollbar-hide">
          {weeks.map((w, i) => {
            const isLocked = w.week > currentWeek;
            const isComplete = w.week < currentWeek;
            const isCurrent = w.week === currentWeek;
            const isSelected = w.week === selectedWeek;
            const done = weekCompletions[w.week] ?? 0;

            return (
              <button
                key={w.week}
                type="button"
                disabled={isLocked}
                onClick={() => { if (!isLocked) { setSelectedWeek(w.week); setShowWeekDetail(true); } }}
                className={`
                  flex-shrink-0 w-[72px] rounded-xl p-2 text-center transition-all duration-200 border
                  ${isLocked ? 'opacity-40 cursor-not-allowed border-gray-200 dark:border-gray-700/50 bg-gray-50 dark:bg-white/5' : ''}
                  ${isComplete && !isSelected ? 'border-emerald-200 dark:border-emerald-800/50 bg-emerald-50 dark:bg-emerald-900/15 cursor-pointer' : ''}
                  ${isCurrent && !isSelected ? 'border-purple-500/30 dark:border-purple-500/40 bg-purple-600/5 dark:bg-purple-600/10 cursor-pointer' : ''}
                  ${isSelected ? 'border-purple-500 dark:border-purple-500 bg-purple-600/10 dark:bg-purple-600/20 ring-1 ring-purple-500/30' : ''}
                  ${!isLocked && !isComplete && !isCurrent && !isSelected ? 'border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-800 cursor-pointer hover:border-purple-500/20' : ''}
                `}
              >
                <span className="text-sm mb-0.5 block">{isLocked ? '🔒' : isComplete ? '✅' : WEEK_ICONS[i]}</span>
                <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300 block">{w.week}</span>
                {!isLocked && (
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1 mt-1">
                    <div className={`h-1 rounded-full transition-all ${isComplete ? 'bg-emerald-500' : 'bg-purple-600'}`} style={{ width: `${(done / 7) * 100}%` }} />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Tap a week prompt when detail is hidden */}
      {!showWeekDetail && (
        <div className="rounded-xl bg-purple-50 dark:bg-purple-900/10 border border-purple-200 dark:border-purple-800/30 p-4 text-center">
          <GraduationCap className="w-8 h-8 text-purple-500 mx-auto mb-2" />
          <p className="text-sm font-medium text-gray-700 dark:text-gray-300" style={{ fontFamily: 'Sora, sans-serif' }}>
            {t('training.tapWeekToContinue', 'Tap Week {{week}} to continue your training', { week: currentWeek })}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
            {t('training.completeToUnlock', 'Complete daily tasks to unlock the next week')}
          </p>
          <button
            type="button"
            onClick={() => { setSelectedWeek(currentWeek); setShowWeekDetail(true); }}
            className="mt-3 inline-flex items-center gap-1.5 px-5 py-2 bg-purple-600 text-white rounded-lg text-sm font-semibold hover:bg-purple-700 transition-colors"
            style={{ fontFamily: 'Sora, sans-serif' }}
          >
            {t('training.continueWeek', 'Continue Week {{week}}', { week: currentWeek })} <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Selected Week Tasks */}
      {showWeekDetail && <div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => { setShowWeekDetail(false); setShowEnrolledDashboard(false); }}
              className="w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center text-gray-500 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-lg">{WEEK_ICONS[selectedWeek - 1]}</span>
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight" style={{ fontFamily: 'Sora, sans-serif' }}>
                {t('training.weekTitle', 'Week {{week}}: {{title}}', { week: selectedWeek, title: t(`training.week${selectedWeek}Title`, weeks[selectedWeek - 1]?.title) })}
              </h3>
              <p className="text-xs text-gray-400 dark:text-gray-500">{t(`training.week${selectedWeek}Theme`, weeks[selectedWeek - 1]?.theme)}</p>
            </div>
          </div>
          <span className="text-xs font-semibold text-purple-600 dark:text-purple-400 bg-purple-600/10 dark:bg-purple-600/15 px-2.5 py-1 rounded-full">
            {t('training.done', '{{done}}/{{total}} done', { done: currentTasks.filter((tk) => completedTasks.has(tk.id)).length, total: currentTasks.length })}
          </span>
        </div>

        {isLoadingProgress ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="w-5 h-5 animate-spin text-purple-600" />
          </div>
        ) : currentTasks.length === 0 ? (
          <div className="text-center py-10 rounded-xl bg-gray-50 dark:bg-white/5 border border-dashed border-gray-200 dark:border-gray-700/50">
            <Lock className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
            <p className="text-xs text-gray-400 dark:text-gray-500">{t('training.unlockPreviousWeek', 'Complete the previous week to unlock')}</p>
          </div>
        ) : (
          <div className="space-y-2">
            {currentTasks.map((task) => {
              const done = completedTasks.has(task.id);
              const loading = completingTaskId === task.id;
              return (
                <div
                  key={task.id}
                  className={`
                    rounded-xl px-4 py-3 transition-all duration-200 border
                    ${done
                      ? 'bg-emerald-50 dark:bg-emerald-900/10 border-emerald-200 dark:border-emerald-800/40'
                      : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700/50 hover:border-purple-500/30'
                    }
                  `}
                >
                  <div className="flex items-center gap-3">
                    {/* Day badge */}
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 text-xs font-bold text-white ${done ? 'bg-emerald-500' : ''}`} style={{ fontFamily: 'Sora, sans-serif', ...(!done ? { background: 'linear-gradient(135deg, #594BFF, #8B5CF6)' } : {}) }}>
                      {done ? <Check className="w-4 h-4" /> : <>D{task.day}</>}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h4 className={`text-sm font-medium ${done ? 'text-emerald-700 dark:text-emerald-400 line-through' : 'text-gray-900 dark:text-white'}`} style={{ fontFamily: 'Sora, sans-serif' }}>
                        {t(`training.task_${task.id}_title`, task.title)}
                      </h4>
                      <p className={`text-xs mt-0.5 ${done ? 'text-emerald-600/60 dark:text-emerald-400/50' : 'text-gray-400 dark:text-gray-500'}`}>
                        {t(`training.task_${task.id}_desc`, task.description)}
                      </p>
                    </div>
                  </div>

                  {/* Bottom row: meta + action */}
                  <div className="flex items-center justify-between mt-2.5 pl-12">
                    <div className="flex items-center gap-3">
                      <span className="text-xs text-gray-400 dark:text-gray-500 flex items-center gap-1">
                        <Clock className="w-3 h-3" />{t('training.durationMin', '{{mins}} min', { mins: task.durationMinutes })}
                      </span>
                      {task.pointsReward > 0 && (
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${done ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400' : 'bg-purple-600/10 text-purple-600 dark:text-purple-400'}`}>
                          +{task.pointsReward} PETS
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={done || loading}
                      onClick={() => !done && !loading && handleCompleteTask(task.id)}
                      className={`
                        inline-flex items-center gap-1.5 px-4 py-1.5 rounded-lg text-xs font-semibold transition-all
                        ${done
                          ? 'bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 cursor-default'
                          : loading
                            ? 'bg-purple-100 dark:bg-purple-900/30 text-purple-500 cursor-wait'
                            : 'bg-purple-600 text-white hover:bg-purple-700 active:scale-95 cursor-pointer shadow-sm'
                        }
                      `}
                      style={{ fontFamily: 'Sora, sans-serif' }}
                    >
                      {loading ? (
                        <><Loader2 className="w-3 h-3 animate-spin" /> {t('training.saving', 'Saving...')}</>
                      ) : done ? (
                        <><CheckCircle2 className="w-3 h-3" /> {t('training.completed', 'Completed')}</>
                      ) : (
                        <><Check className="w-3 h-3" /> {t('training.markComplete', 'Mark Complete')}</>
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>}

      {/* Badges Preview */}
      {(progress?.badges?.length ?? 0) > 0 && (
        <div className="rounded-xl bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700/50 p-4">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-2" style={{ fontFamily: 'Sora, sans-serif' }}>{t('training.badges', 'Badges')}</h3>
          <div className="flex flex-wrap gap-2">
            {progress?.badges?.map((b, i) => (
              <div key={i} className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg bg-amber-50 dark:bg-amber-900/15 border border-amber-200 dark:border-amber-800/40">
                <span className="text-sm">{b.icon || '🏆'}</span>
                <span className="text-[11px] font-medium text-amber-700 dark:text-amber-400">{b.name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
